import type { IChiefEditorsResponse } from "~/types/IChiefEditorsResponse";
import { useCacheService } from "~/composables/useCacheService";

export const useSearchService = () => {
  const { searchApiUrl } = useRuntimeConfig().public;
  const { appUserToken } = useRuntimeConfig();
  const { getFormerKey, cache, LONG_TTL } = useCacheService();

  const getChiefEditorsByJournalId = async (
    journalId: number
  ): Promise<IChiefEditorsResponse[]> => {
    const cacheKey = getFormerKey("api", "search:chief-editors", journalId);

    return cache(
      cacheKey,
      async () => {
        try {
          return await $fetch<IChiefEditorsResponse[]>(
            `${searchApiUrl}/api/v1/search/journals/${journalId}/chief-editors`,
            {
              headers: {
                Authorization: `Basic ${appUserToken}`
              }
            }
          );
        } catch (error: unknown) {
          const err = error as { statusCode?: number; message?: string };
          throw createError({
            statusCode: err.statusCode || 500,
            message:
              err.message ||
              "Error fetching chief editors from search API"
          });
        }
      },
      LONG_TTL
    );
  };

  const getChiefEditorsBySectionId = async (
    journalId: number,
    sectionId: number
  ): Promise<IChiefEditorsResponse[]> => {
    const cacheKey = getFormerKey(
      "api",
      "search:chief-editors",
      `${journalId}:${sectionId}`
    );

    return cache(
      cacheKey,
      async () => {
        try {
          return await $fetch<IChiefEditorsResponse[]>(
            `${searchApiUrl}/api/v1/search/journals/${journalId}/sections/${sectionId}/chief-editors`,
            {
              headers: {
                Authorization: `Basic ${appUserToken}`
              }
            }
          );
        } catch (error: unknown) {
          const err = error as { statusCode?: number; message?: string };
          throw createError({
            statusCode: err.statusCode || 500,
            message: `Error fetching chief editors for journal ${journalId} section ${sectionId} from search API`
          });
        }
      },
      LONG_TTL
    );
  };

  return {
    getChiefEditorsByJournalId,
    getChiefEditorsBySectionId
  };
};
